/**
 * The projective map between an arbitrary quadrilateral and a unit square.
 *
 * ## Why the frame needs one
 *
 * A site frame started as a north-up square, which is fine only when the plot happens to sit square
 * to north — and almost none do. Once the frame can be **rotated**, or its corners dragged into a
 * genuine quadrilateral, the relationship between "a point on Earth" and "a point in the reference
 * image" stops being a scale and an offset.
 *
 * A homography is the smallest thing that covers all three cases with one piece of arithmetic:
 *
 * - **Axis-aligned rectangle** — reduces to a scale and translate, exactly.
 * - **Rotated rectangle** — reduces to a similarity, exactly. No approximation, no accumulated error
 *   from repeated rotations, because the map is rebuilt from the corners each time rather than
 *   composed.
 * - **Free quadrilateral** — the general case, which is what "drag the corners" actually means.
 *
 * Everything else in `geo/` then has one conversion to call and no special cases: `frameToSite`,
 * `frameToImage`, the parcel overlay, the elevation import and the capture's rectification all go
 * through the same matrix.
 *
 * ## What it is honest about
 *
 * A homography maps a quadrilateral onto a square. It does **not** make a trapezoidal plot into a
 * rectangular one *without consequence*: aerial imagery is already orthographic, so a quad whose
 * sides really are unequal describes ground whose sides really are unequal, and stretching it to
 * fill a square resamples that ground non-uniformly. That is a legitimate thing to want — the site
 * model is a rectangular heightfield, and framing an awkward plot to fill it beats framing it inside
 * a square with two corners of the neighbour's garden — but it is a *choice*, so `MapFrame` measures
 * how far from rectangular a quad has drifted and says so, rather than quietly scaling the ground.
 *
 * Pure arithmetic. No DOM, no imagery, no knowledge of what the points mean.
 */

/** A 3×3 projective transform, row-major. */
export type Matrix3 = readonly [number, number, number, number, number, number, number, number, number];

export interface Point2 {
  x: number;
  y: number;
}

/** The identity, for a frame that has not been shaped. */
export const IDENTITY_MATRIX3: Matrix3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/**
 * The transform taking four source points to four destination points.
 *
 * Solves the standard 8-unknown system with `h22` fixed at 1, which is well conditioned for every
 * quad this app can produce (a frame is convex and never near-degenerate — {@link isConvexQuad}
 * refuses the ones that would be). Returns null rather than a matrix of `NaN` when the system is
 * singular, so a bad drag is a rejected drag instead of a site that silently disappears.
 */
export function solveHomography(
  source: readonly [Point2, Point2, Point2, Point2],
  destination: readonly [Point2, Point2, Point2, Point2],
): Matrix3 | null {
  // Each correspondence contributes two rows:
  //   x·h00 + y·h01 + h02 − u·x·h20 − u·y·h21 = u
  //   x·h10 + y·h11 + h12 − v·x·h20 − v·y·h21 = v
  const a: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i += 1) {
    const s = source[i] as Point2;
    const d = destination[i] as Point2;
    a.push([s.x, s.y, 1, 0, 0, 0, -d.x * s.x, -d.x * s.y]);
    b.push(d.x);
    a.push([0, 0, 0, s.x, s.y, 1, -d.y * s.x, -d.y * s.y]);
    b.push(d.y);
  }

  const h = solveLinearSystem(a, b);
  if (!h) return null;
  return [
    h[0] as number, h[1] as number, h[2] as number,
    h[3] as number, h[4] as number, h[5] as number,
    h[6] as number, h[7] as number, 1,
  ];
}

/** Apply a transform to a point. Returns null where the point lands on the horizon. */
export function applyMatrix3(matrix: Matrix3, point: Point2): Point2 | null {
  const w = matrix[6] * point.x + matrix[7] * point.y + matrix[8];
  // A vanishing w means the point projects to infinity. For a convex frame this only happens well
  // outside it, and returning null lets the caller treat that as "outside" rather than as a
  // coordinate of ±1e17.
  if (Math.abs(w) < 1e-12) return null;
  return {
    x: (matrix[0] * point.x + matrix[1] * point.y + matrix[2]) / w,
    y: (matrix[3] * point.x + matrix[4] * point.y + matrix[5]) / w,
  };
}

/** The inverse transform, or null when the matrix is not invertible. */
export function invertMatrix3(m: Matrix3): Matrix3 | null {
  const [a, b, c, d, e, f, g, h, i] = m;
  const cofactorA = e * i - f * h;
  const cofactorB = f * g - d * i;
  const cofactorC = d * h - e * g;
  const determinant = a * cofactorA + b * cofactorB + c * cofactorC;
  if (Math.abs(determinant) < 1e-14) return null;

  const inv = 1 / determinant;
  return [
    cofactorA * inv,
    (c * h - b * i) * inv,
    (b * f - c * e) * inv,
    cofactorB * inv,
    (a * i - c * g) * inv,
    (c * d - a * f) * inv,
    cofactorC * inv,
    (b * g - a * h) * inv,
    (a * e - b * d) * inv,
  ];
}

/** The unit square's corners, in the frame's own order: top-left, top-right, bottom-right, bottom-left. */
export const UNIT_SQUARE: readonly [Point2, Point2, Point2, Point2] = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

/** The transform taking a quad's corners onto the unit square — a frame's "where am I in the image". */
export function quadToUnitSquare(
  corners: readonly [Point2, Point2, Point2, Point2],
): Matrix3 | null {
  return solveHomography(corners, UNIT_SQUARE);
}

/** The inverse: unit-square coordinates back onto the quad. */
export function unitSquareToQuad(
  corners: readonly [Point2, Point2, Point2, Point2],
): Matrix3 | null {
  return solveHomography(UNIT_SQUARE, corners);
}

/**
 * Whether a quad is convex and wound consistently.
 *
 * The check a corner drag has to pass. A concave or self-crossing quad still *solves* — the algebra
 * does not care — and produces a transform that folds the image over itself, so the failure surfaces
 * much later as a site with a mirrored corner. Rejecting the drag at the handle is the only place
 * this is cheap to explain.
 */
export function isConvexQuad(corners: readonly Point2[], minCross = 1e-9): boolean {
  if (corners.length !== 4) return false;
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const a = corners[i] as Point2;
    const b = corners[(i + 1) % 4] as Point2;
    const c = corners[(i + 2) % 4] as Point2;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < minCross) return false;
    const current = Math.sign(cross);
    if (sign === 0) sign = current;
    else if (current !== sign) return false;
  }
  return true;
}

/** Shoelace area of a quad, sign-free. */
export function quadArea(corners: readonly Point2[]): number {
  let sum = 0;
  for (let i = 0; i < corners.length; i += 1) {
    const a = corners[i] as Point2;
    const b = corners[(i + 1) % corners.length] as Point2;
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/** Gaussian elimination with partial pivoting. Small and square, so nothing fancier is warranted. */
function solveLinearSystem(a: number[][], b: number[]): number[] | null {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i] as number]);

  for (let column = 0; column < n; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < n; row += 1) {
      if (Math.abs((m[row] as number[])[column] as number) > Math.abs((m[pivot] as number[])[column] as number)) {
        pivot = row;
      }
    }
    const pivotRow = m[pivot] as number[];
    if (Math.abs(pivotRow[column] as number) < 1e-12) return null;

    [m[column], m[pivot]] = [pivotRow, m[column] as number[]];
    const top = m[column] as number[];
    const scale = top[column] as number;
    for (let k = column; k <= n; k += 1) top[k] = (top[k] as number) / scale;

    for (let row = 0; row < n; row += 1) {
      if (row === column) continue;
      const target = m[row] as number[];
      const factor = target[column] as number;
      if (factor === 0) continue;
      for (let k = column; k <= n; k += 1) {
        target[k] = (target[k] as number) - factor * (top[k] as number);
      }
    }
  }

  return m.map((row) => (row)[n] as number);
}
